import type { AICheckerTrace, AiCheckerEmailHandoffFiles } from '../../../../../services/admin/adminService';

export function buildAICheckerEmailPrompt(trace: AICheckerTrace): string {
  const typeLabel =
    trace.triageCategory === 'new_supplier' ? 'New supplier'
    : trace.triageCategory === 'needs_attention' ? 'Should have processed'
    : trace.emailVerdict === 'wrong' ? 'Wrong total'
    : trace.matchedIn === 'revenue' && trace.hasIssue ? 'Revenue issue'
    : 'Issue';

  const lines = [
    `Type: ${typeLabel}`,
    `Mailbox: ${trace.account}`,
    `From: ${trace.emailFrom || '-'}`,
    `Subject: ${trace.emailSubject || '-'}`,
    `Platform: ${trace.platform || 'unknown'}`,
    trace.restaurantName ? `Restaurant: ${trace.restaurantName}` : null,
    trace.orderNumbers.length > 0 ? `Order numbers: ${trace.orderNumbers.join(', ')}` : null,
    trace.storedTotal != null ? `Stored total: €${trace.storedTotal.toFixed(2)}` : null,
    trace.triageReason ? `Reason: ${trace.triageReason}` : null,
    trace.manualIssue ? 'Manually flagged: yes — a super-admin marked this email as an issue; the automatic check did NOT flag it.' : null,
    trace.emailIssues.length > 0 ? `Issues:\n- ${trace.emailIssues.join('\n- ')}` : null,
    trace.rfc822MessageId ? `Find the email in Gmail: rfc822msgid:${trace.rfc822MessageId}` : null,
  ].filter(Boolean);

  let guidance = '';
  if (trace.triageCategory === 'new_supplier') {
    guidance = 'This looks like a supplier we do not support yet. Follow @z_playbooks/roles/addSupplier.md to add support, then reprocess this email.';
  } else if (trace.triageCategory === 'needs_attention') {
    guidance = 'A supported supplier email that produced no order. Investigate routing / the platform handler; once fixed, reprocess this email via the processMessageIds endpoint.';
  } else if (trace.emailVerdict === 'wrong') {
    guidance = 'The stored order total does not match the invoice. Investigate the platform parser for this supplier, then reprocess this email.';
  } else if (trace.matchedIn === 'revenue' && trace.hasIssue) {
    guidance = 'This revenue email was stored but failed one or more revenue checks (see Issues above): a non-zero category matching no configured section (incomplete revenue for those days), a Scanfie product with no product-group mapping (excluded from the stored revenue), empty/€0 stored revenue, or a sanity failure (negative amount / ex-VAT exceeding gross). For an unmapped Scanfie product, add it to the mapping in server/domains/revenueData/sources/scanfie/scanfieProductGroupConfig.ts. Otherwise investigate the relevant section config or the platform revenue parser. Then reprocess this email.';
  } else if (trace.manualIssue) {
    guidance = 'The automatic check cleared this email but a super-admin disagrees. Assume the operator is right: work out what the check missed (routing, triage rules, the platform handler or the parser), and say explicitly what should have happened with this email.';
  }

  return `@README.md AI Checker flagged this email:\n\n${lines.join('\n')}\n\n${guidance}`;
}

/**
 * The "check wrong processing" case: everything in the row's detail modal, the outcome of any check
 * already run on the row (deep checks are not stored, so the chip is the only record), and the text of
 * the email's files. A `File text` line at the start of a line tells the listener the text is inline.
 */
export function buildWrongProcessingCaseInfo(
  trace: AICheckerTrace,
  { bodyText, files }: AiCheckerEmailHandoffFiles,
  rowChecks: string[],
): string {
  const facts = [
    `Mailbox: ${trace.account}`,
    `From: ${trace.emailFrom || '-'}`,
    `Subject: ${trace.emailSubject || '-'}`,
    `Platform: ${trace.platform || 'unknown'}`,
    trace.restaurantName ? `Restaurant: ${trace.restaurantName}` : null,
    trace.orderNumbers.length > 0 ? `Order numbers: ${trace.orderNumbers.join(', ')}` : null,
    trace.storedTotal != null ? `Stored total: €${trace.storedTotal.toFixed(2)}` : null,
    trace.emailVerdict ? `Checker verdict: ${trace.emailVerdict}` : null,
    trace.triageReason ? `Reason: ${trace.triageReason}` : null,
    trace.emailIssues.length > 0 ? `Issues:\n- ${trace.emailIssues.join('\n- ')}` : null,
    trace.emailReasoning ? `Checker reasoning:\n${trace.emailReasoning}` : null,
    trace.factuurExpected != null ? `Factuur expected: ${trace.factuurExpected}, created: ${trace.factuurCreated}, factuur orders: ${trace.factuurOrdersCount ?? 0}` : null,
    rowChecks.length > 0 ? `Checks just run on this email:\n- ${rowChecks.join('\n- ')}` : null,
    trace.gmailMessageId ? `Gmail message id: ${trace.gmailMessageId}` : null,
    trace.rfc822MessageId ? `RFC822 message id: ${trace.rfc822MessageId}` : null,
  ].filter(Boolean);

  const fileBlocks = files.map((f) => (f.fileText
    ? `File text (${f.filename}):\n${f.fileText}`
    : `${f.filename}: no text included here${f.dataBase64 ? '; read the attached file' : ' and too large to attach; ask the operator for it'}.`));
  const body = bodyText.trim() ? [`Email body:\n${bodyText.trim()}`] : [];

  return [facts.join('\n'), ...body, ...fileBlocks].join('\n\n');
}
